import { HttpClient } from '@angular/common/http';
import { Directive, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { withoutViewport } from '../../../models/search-request';
import { selectAuthUser } from '../../../store/selectors/auth.selectors';
import { selectCarState } from '../../../store/selectors/car.selectors';
import { selectRealEstateState } from '../../../store/selectors/real-estate.selectors';
import { rectangle } from '../../../saved-searches/saved-search.model';
import { DropdownCheckboxSelectComponent } from './dropdown-checkbox-select.component';

interface FacetResponse {
  facets: Record<string, { options: { value: string; count: number }[] }>;
}

/** Opt-in on search selectors only; never dispatches listing-search actions. */
@Directive({ selector: 'app-dropdown-checkbox-select[searchFacet]', standalone: true })
export class SearchFacetCountsDirective implements OnInit, OnDestroy {
  @Input({ required: true }) searchFacet!: string;
  @Input({ required: true }) facetDomain!: 'CAR' | 'REAL_ESTATE';
  @Input({ required: true }) facetForm!: FormGroup;
  private readonly selector = inject(DropdownCheckboxSelectComponent, { self: true });
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private opened?: Subscription;
  private request?: Subscription;

  ngOnInit(): void {
    this.opened = this.selector.openChanged.subscribe((open) => {
      this.request?.unsubscribe();
      this.selector.setFacetCounts(null, open ? 'loading' : 'idle');
      if (!open) return;
      const mode$ =
        this.facetDomain === 'CAR'
          ? this.store
              .select(selectCarState)
              .pipe(
                map((s) => ({ isMap: s.isCarMapView, polygon: s.activePolygon, query: s.mapQuery }))
              )
          : this.store
              .select(selectRealEstateState)
              .pipe(
                map((s) => ({
                  isMap: s.isRealEstateMapView,
                  polygon: s.activePolygon,
                  query: s.mapQuery,
                }))
              );
      this.request = combineLatest([
        this.facetForm.valueChanges.pipe(startWith(null)),
        mode$,
        this.store.select(selectAuthUser),
      ])
        .pipe(
          map(([, mode, user]) => {
            const criteria: Record<string, unknown> = {
              ...withoutViewport(this.facetForm.getRawValue()),
              ...(mode.isMap
                ? mode.polygon?.length
                  ? { polygonFilter: mode.polygon }
                  : rectangle(mode.query)
                : {}),
            };
            // The server also excludes this facet. Excluding it from the request key avoids
            // needless refetches while selecting options in this disjunctive group.
            delete criteria[this.searchFacet];
            for (const key of ['limit', 'offset', 'sort', 'direction']) delete criteria[key];
            return { criteria, scope: [mode.isMap, user?.id, user?.role] };
          }),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          switchMap(({ criteria }) => {
            this.selector.setFacetCounts(null, 'loading');
            const controller = this.facetDomain === 'CAR' ? 'Cars' : 'RealEstate';
            return this.http
              .post<FacetResponse>(`${environment.apiUrl}/api/${controller}/search/facets`, {
                criteria,
                facets: [this.searchFacet],
              })
              .pipe(catchError(() => of(null)));
          })
        )
        .subscribe((response) => {
          const options = response?.facets?.[this.searchFacet]?.options;
          this.selector.setFacetCounts(
            options ? Object.fromEntries(options.map((o) => [o.value, o.count])) : null,
            options ? 'ready' : 'error'
          );
        });
    });
  }

  ngOnDestroy(): void {
    this.opened?.unsubscribe();
    this.request?.unsubscribe();
  }
}
